import { LocalDateTimeString } from '@ag-common-lib/lib/models/utils/local-date-time-string.model';
import { format } from 'date-fns';
import { localDateTimeFromTimestamp } from 'ag-common-svc/lib/utils/date-from-timestamp';
import { MMDDYYYY_DATE_FORMAT } from '@ag-common-lib/lib/constants/date.constants';
import { firstValueFrom } from 'rxjs';
import { LookupId, Lookups, ShirtCategory, TShirtCategory } from '@ag-common-lib/public-api';

/**
 * Calculates a numeric weight for a clothing size string to enable logical sorting.
 * Supports sizes like YS, AM, W2XL, M3XS, etc.
 *
 * Sorting order logic:
 * 1. Category (Y < A < W < M)
 * 2. Base Size (XS < S < M < L < XL)
 * 3. Multiplier (3XS < 2XS < XS; XL < 2XL < 3XL)
 *
 * @param sizeValue The size string to calculate weight for (e.g., "M2XL")
 * @returns A numeric weight for sorting or the original string if not a valid size
 */
export const getShirtSizeSortWeight = (sizeValue: string): number | string => {
  const value = String(sizeValue ?? '')
    .toUpperCase()
    .trim();
  if (!value) return value;

  // Category offsets to group Y, A, W, M separately
  const categories = { Y: 10000, A: 20000, W: 30000, M: 40000 };

  // Base weights for standard sizes
  const baseSizes = { XS: 100, S: 200, M: 300, L: 400, XL: 500 };

  const firstChar = value[0];
  const categoryWeight = categories[firstChar];

  // If first character is not a known category, return original value for alpha-sort
  if (!categoryWeight) return value;

  const sizePart = value.slice(1);
  if (!sizePart) return categoryWeight;

  let baseSizeWeight = 0;
  let foundBase = '';

  // Find the base size (matching from the end of the string)
  for (const [size, weight] of Object.entries(baseSizes)) {
    if (sizePart.endsWith(size)) {
      // Pick the longest match (e.g., 'XL' instead of 'L')
      if (size.length > foundBase.length) {
        baseSizeWeight = weight;
        foundBase = size;
      }
    }
  }

  if (foundBase) {
    const prefix = sizePart.slice(0, sizePart.length - foundBase.length);

    if (foundBase.includes('X')) {
      // Extract numeric multiplier (e.g., '2' from '2XL')
      const numericPart = prefix.replace('X', '');
      // If no number before X (e.g., 'MXL'), default to 1
      const multiplier = numericPart ? parseInt(numericPart, 10) : 1;

      if (foundBase === 'XS') {
        // For XS: more X's means smaller size (3XS < 2XS < XS)
        baseSizeWeight -= multiplier;
      } else {
        // For XL: more X's means larger size (XL < 2XL < 3XL)
        baseSizeWeight += multiplier;
      }
    }
  } else {
    // Fallback for custom text after category prefix
    return categoryWeight + sizePart.charCodeAt(0);
  }

  return categoryWeight + baseSizeWeight;
};


/**
 * Extracts shirt category from size string based on first character (A=Adult, Y=Youth, M=Men, W=Women).
 */
export function getShirtCategoryFromSize(sizeValue: LookupId<"TShirtAdult"> | LookupId<"TShirtYouth"> | string): TShirtCategory | ShirtCategory {
  if (!sizeValue) {
    return null;
  }

  const categoryLetter = sizeValue?.charAt(0).toUpperCase();

  switch (categoryLetter) {
    case 'A':
      return TShirtCategory.adult;
    case 'Y':
      return TShirtCategory.youth;
    case 'M':
      return ShirtCategory.men;
    case 'W':
      return ShirtCategory.women;
    default:
      return TShirtCategory.adult;
  }
}


/**
 * Maps shirt category enum to corresponding lookup name for retrieving size options.
 */
export function getLookupNameByCategory(category: TShirtCategory | ShirtCategory): Lookups | null {
  switch (category) {
    case TShirtCategory.adult:
      return Lookups.TShirtAdult;
    case TShirtCategory.youth:
      return Lookups.TShirtYouth;
    case ShirtCategory.men:
      return Lookups.ShirtMen;
    case ShirtCategory.women:
      return Lookups.ShirtWomen;
    default:
      return null;
  }
}
